import type { IdentificationData } from "@pandas/http-client";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import { useBarcodeScanner } from "@/components/imei-scan/hooks/useBarcodeScanner";
import { useCameraStream } from "@/components/imei-scan/hooks/useCameraStream";
import { useCaptureVideoFrame } from "@/components/imei-scan/hooks/useCaptureVideoFrame";
import { useImeiInfo } from "@/components/imei-scan/hooks/useImeiInfo";
import { useNativeZoomOnScan } from "@/components/imei-scan/hooks/useNativeZoomOnScan";
import type { CustomVideoElement } from "@/components/imei-scan/types";
import AvailableCapacitiesPopup from "@/components/imei-scan/ui/AvailableCapacitiesPopup";
import { AvailableProductsPopup } from "@/components/imei-scan/ui/AvailableProductsPopup";
import ImeiInfoPopup from "@/components/imei-scan/ui/ImeiInfoPopup";
import ImeiNotFoundPopup from "@/components/imei-scan/ui/ImeiNotFoundPopup";
import ImeiScannerResult from "@/components/imei-scan/ui/ImeiScannerResult";
import ManualImeiInputPopup from "@/components/imei-scan/ui/ManualImeiInputPopup";
import Sparkles from "@/components/sparkles";
import useDarkMode from "@/hooks/useDarkMode";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { PlatformError } from "@/hooks/usePlatformErrors";
import {
	getCameraProvider,
	ImeiCandidateWindow,
	NativeCameraSlot,
	nativeCamera,
	slotAspectOf,
	useNativeCameraSnapshot,
} from "@/lib/camera";
import logger from "@/lib/logging/logger";
import type {
	NativeBarcodeSymbology,
	NativeCameraBarcodePayload,
	NativeCameraTextPayload,
	NativeTextPreset,
} from "@/lib/native-bridge";
import { useStore } from "@/store";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { isValidIMEI } from "@/utils/validators";

export const Route = createFileRoute("/$locale/$partner/_app/_card/imei-scan")({
	component: ImeiScanPage,
});

// The browser path lets ZBar decode every symbology it knows, so native detection asks for the full set.
const IMEI_BARCODE_SYMBOLOGIES: NativeBarcodeSymbology[] = [
	"code128",
	"code39",
	"code93",
	"ean13",
	"ean8",
	"upce",
	"itf14",
	"qr",
	"pdf417",
	"datamatrix",
	"aztec",
];
// The wrapper also reads the IMEI printed on the *#06# screen, which Android phones show without a barcode.
const IMEI_TEXT_PRESETS: NativeTextPreset[] = ["imei"];

function ImeiScanPage() {
	const { t } = useTranslation();
	const isProcessing = useRef<boolean>(false);
	const cameraProvider = getCameraProvider();

	const videoRef = useRef<CustomVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const imeiInProcess = useRef<string | null>(null);
	const [showManualImeiInputPopup, setShowManualImeiInputPopup] = useState<boolean>(false);
	const [imeiNotFound, setImeiNotFound] = useState<boolean>(false);
	const [capacity, setCapacity] = useState<string | null>(null);
	const [hasShownImeiNotFound, setHasShownImeiNotFound] = useState(false);
	const scanningPausedRef = useRef(false);
	const acceptImeiCandidateRef = useRef<(value: string) => void>(() => {});
	// Native reads are collected and ranked by the window; an accepted one enters the same scan
	// handler as the browser path's frame loop, under the same gate.
	const [imeiWindow] = useState(
		() =>
			new ImeiCandidateWindow({
				isValid: isValidIMEI,
				isBlocked: () => scanningPausedRef.current || isProcessing.current,
				supportsText: () => nativeCamera.getSnapshot().capabilities?.text === true,
				slotAspect: () => slotAspectOf(nativeCamera.getSnapshot().geometry),
				onHold: (held) => logger.info("INFO", "IMEI candidates held", held),
				onAccept: (read) => {
					logger.info("INFO", "IMEI candidate chosen", read);
					acceptImeiCandidateRef.current(read.value);
				},
			}),
	);
	const cameraFacingMode = useStore((state) => state.config?.pagesConfig.imeiScan.cameraFacingMode) || "user";
	const cameraResolution = { width: 1024, height: 768 };
	const error = useStore((state) => state.error);
	const setError = useStore((state) => state.setError);
	const { goToNextStep, steps } = useNavigationHelpers();
	const maxOfferPageConfig = steps.includes("max-price");

	const abortControllerRef = useRef<AbortController>(new AbortController());
	useEffect(() => {
		abortControllerRef.current = new AbortController();
		return () => {
			abortControllerRef.current.abort();
			imeiWindow.dispose();
		};
	}, [imeiWindow]);

	const { fetchingImeiInfo, imeiInfo, setFetchingImeiInfo, fetchImeiInfo } = useImeiInfo(abortControllerRef);

	const { isScanningPaused, setIsScanningPaused, processFrame } = useBarcodeScanner(
		canvasRef,
		videoRef,
		isProcessing,
		setError,
		handleScan,
		cameraFacingMode,
	);
	const { captureVideoFrame } = useCaptureVideoFrame(
		canvasRef,
		videoRef,
		isProcessing,
		scanningPausedRef,
		processFrame,
		cameraFacingMode,
	);
	const {
		cameraError: browserCameraError,
		isInitializing: browserIsInitializing,
		initializeCameraStream,
	} = useCameraStream(cameraFacingMode, videoRef, captureVideoFrame, cameraResolution, cameraProvider === "browser");
	const nativeCameraState = useNativeCameraSnapshot();
	useNativeZoomOnScan(cameraProvider === "native");
	useDarkMode();

	const cameraError = cameraProvider === "native" ? nativeCameraState.status === "error" : browserCameraError !== null;
	const isInitializing =
		cameraProvider === "native"
			? nativeCameraState.status === "idle" || nativeCameraState.status === "starting"
			: browserIsInitializing;
	const cameraLive =
		cameraProvider === "native" ? nativeCameraState.status === "ready" : Boolean(videoRef.current?.srcObject);
	const retryCamera = cameraProvider === "native" ? () => nativeCamera.restart() : initializeCameraStream;

	// biome-ignore lint/correctness/useExhaustiveDependencies: captureVideoFrame intentionally excluded — it is not memoized and would restart the animation loop on every render
	useEffect(() => {
		scanningPausedRef.current = isScanningPaused;

		if (!isScanningPaused && cameraProvider === "browser") {
			requestAnimationFrame(captureVideoFrame);
		}
	}, [isScanningPaused, cameraProvider]);

	useEffect(() => {
		if (
			showManualImeiInputPopup ||
			imeiInProcess.current ||
			hasShownImeiNotFound ||
			imeiInfo ||
			error ||
			isScanningPaused
		)
			return;

		const timer = setTimeout(() => {
			// A native read still waiting in its window is decided before the prompt would discard it.
			if (imeiWindow.flush()) return;
			setIsScanningPaused(true);
			setImeiNotFound(true);
			setHasShownImeiNotFound(true);
		}, 18000);

		return () => clearTimeout(timer);
	}, [
		error,
		hasShownImeiNotFound,
		imeiInfo,
		imeiWindow,
		isScanningPaused,
		setIsScanningPaused,
		showManualImeiInputPopup,
	]);

	const onContinue = useCallback(() => {
		goToNextStep();
	}, [goToNextStep]);

	const shouldSkipToMaxPrice = (data: IdentificationData): boolean => {
		const noOpts = !data.availableCapacities?.length && !data.availableProducts?.length;
		return maxOfferPageConfig && noOpts;
	};

	const onSubmitManuallyImei = async () => {
		logger.info("FLOW", `Submitting IMEI manually`);
		setShowManualImeiInputPopup(false);
		if (imeiInProcess.current) {
			setIsScanningPaused(true);
			try {
				const data = await fetchImeiInfo(imeiInProcess.current);
				if (abortControllerRef.current.signal.aborted) return;
				if (shouldSkipToMaxPrice(data)) {
					goToNextStep();
					return;
				}
				setFetchingImeiInfo(false);
			} catch (error) {
				if (abortControllerRef.current.signal.aborted) return;
				imeiInProcess.current = null;
				setError(new PlatformError(getErrorMessage(error), () => setIsScanningPaused(false)));
			}
		}
	};

	async function handleScan(imeiCandidate: string) {
		// A code that is not an IMEI is decoded again on every frame, so only an accepted read is logged.
		if (!isValidIMEI(imeiCandidate) || imeiInProcess.current) {
			setIsScanningPaused(false);
			return;
		}
		logger.info("INFO", `Detected IMEI ${imeiCandidate}`);
		imeiInProcess.current = imeiCandidate;
		try {
			const data = await fetchImeiInfo(imeiCandidate);
			if (abortControllerRef.current.signal.aborted) return;
			if (shouldSkipToMaxPrice(data)) {
				goToNextStep();
				return;
			}
			setIsScanningPaused(true);
		} catch (error) {
			if (abortControllerRef.current.signal.aborted) return;
			imeiInProcess.current = null;
			setError(
				new PlatformError(
					getErrorMessage(error),
					() => setIsScanningPaused(false),
					() => setShowManualImeiInputPopup(true),
				),
			);
		}
	}
	const acceptImeiCandidate = async (value: string) => {
		isProcessing.current = true;
		try {
			setIsScanningPaused(true);
			await handleScan(value);
		} finally {
			isProcessing.current = false;
		}
	};
	acceptImeiCandidateRef.current = (value) => void acceptImeiCandidate(value);
	const onNativeBarcode = (read: NativeCameraBarcodePayload) => imeiWindow.addBarcode(read);
	const onNativeText = (read: NativeCameraTextPayload) => imeiWindow.addText(read);

	const noOptions = imeiInfo && !imeiInfo?.availableCapacities?.length && !imeiInfo?.availableProducts?.length;
	const showImeiInfoPopup = !maxOfferPageConfig && noOptions;

	return (
		<div className="flex flex-col items-center justify-center h-full text-white text-center">
			{cameraError && (
				<button
					className="bg-zinc-500 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-sm sm:text-base"
					onClick={retryCamera}
				>
					Retry
				</button>
			)}
			{isInitializing && (
				<div className="absolute inset-0 bg-zinc-900 flex flex-col items-center justify-center z-50 gap-2 sm:gap-4"></div>
			)}
			<h4 className="max-w-2xl font-bold">
				{t("camera layout.Align your device with the tablet's front camera to scan the IMEI.")}
			</h4>
			{cameraProvider === "browser" && (
				<video
					ref={videoRef}
					muted
					autoPlay
					playsInline
					controls
					className="hidden w-fit h-fit overflow-hidden"
				/>
			)}
			<div
				className={`w-full relative overflow-hidden h-fit my-6 rounded-xl ${
					cameraProvider === "native" ? "mx-auto max-w-[calc(var(--1dvh)*45)]" : ""
				}`}
			>
				{cameraProvider === "native" ? (
					<NativeCameraSlot
						facing={cameraFacingMode}
						detect={IMEI_BARCODE_SYMBOLOGIES}
						detectText={IMEI_TEXT_PRESETS}
						onBarcode={onNativeBarcode}
						onText={onNativeText}
						className="w-full aspect-[3/4] rounded-xl"
					/>
				) : (
					<canvas
						ref={canvasRef}
						className="h-full rounded-xl overflow-hidden mx-auto max-w-full"
					/>
				)}
				{cameraLive && <Sparkles />}
				<div
					className="absolute left-0 right-0 h-[1px] sm:h-[2px] bg-primary"
					style={{
						top: "50%",
						background: "linear-gradient(90deg, transparent 0%, lime 50%, transparent 100%)",
						animation: "scan 4s ease-in-out infinite",
					}}
				/>
				<img
					src="/images/imei_screen.png"
					className="w-[100px] sm:w-[150px] bottom-0 absolute rounded-xl left-0 right-[75%] mx-auto"
				/>
			</div>
			<Button
				themeMode="dark"
				intent="tertiary"
				label={t("btn.Add IMEI manually")}
				onClick={() => {
					setShowManualImeiInputPopup(true);
					setIsScanningPaused(true);
				}}
			/>
			{fetchingImeiInfo && <ImeiScannerResult scannedImeiRef={imeiInProcess} />}

			{imeiInfo && imeiInfo.availableCapacities && imeiInfo.availableCapacities.length > 0 && (
				<AvailableCapacitiesPopup
					imeiInfo={imeiInfo}
					availableCapacities={imeiInfo.availableCapacities}
					capacity={capacity}
					setCapacity={setCapacity}
					onContinue={onContinue}
					setError={setError}
				/>
			)}
			{imeiInfo && imeiInfo.availableProducts && imeiInfo.availableProducts.length > 0 && (
				<AvailableProductsPopup
					onContinue={onContinue}
					setError={setError}
				/>
			)}
			{showImeiInfoPopup && (
				<ImeiInfoPopup
					imeiInfo={imeiInfo.identification.extracted_metadata!}
					onContinue={onContinue}
				/>
			)}
			{imeiNotFound && (
				<ImeiNotFoundPopup
					setShowManualImeiInputPopup={setShowManualImeiInputPopup}
					setIsScanningPaused={setIsScanningPaused}
					setImeiNotFound={setImeiNotFound}
				/>
			)}
			{showManualImeiInputPopup && (
				<ManualImeiInputPopup
					imeiInProcess={imeiInProcess}
					setShowManualImeiInputPopup={setShowManualImeiInputPopup}
					setIsScanningPaused={setIsScanningPaused}
					onSubmitManuallyImei={onSubmitManuallyImei}
				/>
			)}
		</div>
	);
}
